import Hls, { ErrorData } from 'hls.js';

import { metricsQueue } from 'helpers/metricsQueue';

export const MAX_RETRY_ATTEMPTS = 5;
export const RETRY_WINDOW_MS = 30_000;
export const BASE_BACKOFF_MS = 1_000;
export const MAX_BACKOFF_MS = 15_000;

const getErrorTypeMap = (): Record<string, string> => ({
  [Hls.ErrorTypes.NETWORK_ERROR]: 'network',
  [Hls.ErrorTypes.MEDIA_ERROR]: 'media',
  [Hls.ErrorTypes.KEY_SYSTEM_ERROR]: 'key_system',
  [Hls.ErrorTypes.MUX_ERROR]: 'mux',
});

const getStatusCode = (data: ErrorData): string =>
  String(data.response?.code || 'none');

const getBackoffMs = (attempt: number): number =>
  Math.min(
    BASE_BACKOFF_MS * 2 ** (attempt - 1) + Math.random() * 300,
    MAX_BACKOFF_MS,
  );

export type HlsErrorHandler = ((_: unknown, data: ErrorData) => void) & {
  // Cancels any pending retry/settle timer. Call this when the Hls instance
  // is destroyed from outside this handler (e.g. switching tracks), so a
  // timer scheduled for an abandoned track can't later call startLoad()/
  // recoverMediaError() on it or emit a stray "recovered" metric.
  cleanup: () => void;
};

export const hlsErrorHandler = (
  hlsInstance: Hls,
  onGiveUp?: () => void,
): HlsErrorHandler => {
  let retryCount = 0;
  let hasGivenUp = false;
  let retryTimeout: ReturnType<typeof setTimeout> | null = null;
  let settleTimeout: ReturnType<typeof setTimeout> | null = null;

  const clearTimers = () => {
    if (retryTimeout !== null) clearTimeout(retryTimeout);
    if (settleTimeout !== null) clearTimeout(settleTimeout);
    retryTimeout = null;
    settleTimeout = null;
  };

  const giveUp = (labels: Record<string, string>, attemptsMade: number) => {
    clearTimers();
    metricsQueue.increment('player_hls_error_total', {
      ...labels,
      fatal: 'true',
      outcome: 'gave_up',
      retry_count: String(attemptsMade),
    });
    hasGivenUp = true;
    hlsInstance.destroy();
    onGiveUp?.();
  };

  const retry = (labels: Record<string, string>, recover: () => void) => {
    clearTimers();
    retryCount += 1;

    if (retryCount > MAX_RETRY_ATTEMPTS) {
      giveUp(labels, MAX_RETRY_ATTEMPTS);
      return;
    }

    metricsQueue.increment('player_hls_error_total', {
      ...labels,
      fatal: 'true',
      outcome: 'retrying',
      attempt: String(retryCount),
    });

    retryTimeout = setTimeout(recover, getBackoffMs(retryCount));

    settleTimeout = setTimeout(() => {
      metricsQueue.increment('player_hls_error_total', {
        ...labels,
        fatal: 'true',
        outcome: 'recovered',
        retry_count: String(retryCount),
      });
      retryCount = 0;
    }, RETRY_WINDOW_MS);
  };

  const handler = ((_: unknown, data: ErrorData) => {
    if (hasGivenUp) return;

    const labels = {
      error_type: getErrorTypeMap()[data.type] ?? 'other',
      error_detail: data.details ?? 'unknown',
      status_code: getStatusCode(data),
    };

    if (!data.fatal) {
      metricsQueue.increment('player_hls_error_total', {
        ...labels,
        fatal: 'false',
        outcome: 'non_fatal',
      });
      return;
    }

    switch (data.type) {
      case Hls.ErrorTypes.NETWORK_ERROR:
        retry(labels, () => hlsInstance.startLoad());
        break;
      case Hls.ErrorTypes.MEDIA_ERROR:
        retry(labels, () => hlsInstance.recoverMediaError());
        break;
      default:
        giveUp(labels, 0);
        break;
    }
  }) as HlsErrorHandler;

  handler.cleanup = () => {
    hasGivenUp = true;
    clearTimers();
  };

  return handler;
};
